var _dec, _class, _descriptor, _descriptor2;
function _initializerDefineProperty(target, property, descriptor, context) { if (!descriptor) return; Object.defineProperty(target, property, { enumerable: descriptor.enumerable, configurable: descriptor.configurable, writable: descriptor.writable, value: descriptor.initializer ? descriptor.initializer.call(context) : void 0 }); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }
function _initializerWarningHelper(descriptor, context) { throw new Error('Decorating class property failed. Please ensure that ' + 'transform-class-properties is enabled and runs after the decorators transform.'); }
import moment from 'moment';
import momentTimezone from 'moment-timezone';
import Service, { inject as service } from '@ember/service';
import { assert } from '@ember/debug';
import { feature } from '@square/ember-features/macros';
function asMoment(maybeMoment) {
  if (moment.isMoment(maybeMoment)) {
    return maybeMoment;
  } else {
    return moment(maybeMoment);
  }
}
function defineOrUpdateOrSetLocale(m, localeConfig) {
  if (localeConfig) {
    if (m.locales().includes('sq')) {
      m.updateLocale('sq', localeConfig);
    } else {
      m.defineLocale('sq', localeConfig);
    }
  } else {
    m.locale('sq');
  }
}
let LocaleTimeService = (_dec = feature('reportingfe/override_locale_start_of_week'), (_class = class LocaleTimeService extends Service {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "_localizedFormats", null);
    _defineProperty(this, "_formatDate", null);
    _initializerDefineProperty(this, "i18n", _descriptor, this);
    _initializerDefineProperty(this, "override_locale_start_of_week", _descriptor2, this);
  }
  setup(dateFormats) {
    // Some Sellers want to override their start of week to Monday.
    if (this.override_locale_start_of_week) {
      dateFormats.strings.week = {
        dow: 1
      };
    }
    // Configure moment JS for formatting and parsing
    // Some callers also want to change moment's current locale, which they
    // indicate by passing "undefined" here 🤷
    const localeConfig = dateFormats.strings;
    defineOrUpdateOrSetLocale(moment, localeConfig);
    if (momentTimezone !== moment) {
      // 🤷
      defineOrUpdateOrSetLocale(momentTimezone, localeConfig);
    }

    // used to interpolate the 'zz' formatter in i18n/formats
    const tzNames = {
      'Australia/Adelaide': this.i18n.t('timezoneZZ.Australia/Adelaide'),
      'Australia/Brisbane': this.i18n.t('timezoneZZ.Australia/Brisbane'),
      'Australia/Canberra': this.i18n.t('timezoneZZ.Australia/Canberra'),
      'Australia/Darwin': this.i18n.t('timezoneZZ.Australia/Darwin'),
      'Australia/Eucla': this.i18n.t('timezoneZZ.Australia/Eucla'),
      'Australia/Hobart': this.i18n.t('timezoneZZ.Australia/Hobart'),
      'Australia/Melbourne': this.i18n.t('timezoneZZ.Australia/Melbourne'),
      'Australia/Perth': this.i18n.t('timezoneZZ.Australia/Perth'),
      'Australia/Sydney': this.i18n.t('timezoneZZ.Australia/Sydney')
    };
    moment.fn.zoneName = function () {
      return tzNames[this.tz()] || this.zoneAbbr();
    };
    this._formatDate = dateFormats.format;
    this._displaySupportedFormatNames = Object.keys(dateFormats.format).join(', ');
    this._localizedFormats = Object.assign({}, dateFormats.format);
  }

  // localized date formats like 'shortDate' etc.
  dateTime(d, formatName) {
    const formatString = this._formatDate[formatName];
    assert(`Please use a supported format name. You passed ${typeof formatName}. Supported format names are: ${this._displaySupportedFormatNames}.`, formatString);
    const momentObj = asMoment(d);
    return momentObj.format(formatString);
  }
  formatTime(time, {
    hideMeridiem = false,
    zoneType
  } = {}) {
    if (hideMeridiem && zoneType) {
      throw new Error('Zone types are not supported with hiding the meridiem');
    }
    const momentObj = asMoment(time);
    let format;
    if (momentObj.minutes() === 0) {
      format = hideMeridiem ? 'shortTimeZeroMinutesWithoutMeridiem' : zoneType === 'TIME_ZONE' ? 'shortTimeZeroMinutesWithZone' : zoneType === 'OFFSET' ? 'shortTimeZeroMinutesWithOffset' : 'shortTimeZeroMinutes';
    } else {
      format = hideMeridiem ? 'shortTimeWithoutMeridiem' : zoneType === 'TIME_ZONE' ? 'shortTimeWithZone' : zoneType === 'OFFSET' ? 'shortTimeWithOffset' : 'shortTime';
    }
    return this.dateTime(momentObj, format);
  }
  formatWeekdayAndTime(time, {
    hideMeridiem = false
  } = {}) {
    const momentObj = asMoment(time);
    let format;
    if (momentObj.minutes() === 0) {
      format = hideMeridiem ? 'shortWeekdayTimeZeroMinutesWithoutMeridiem' : 'shortWeekdayTimeZeroMinutes';
    } else {
      format = hideMeridiem ? 'shortWeekdayTimeWithoutMeridiem' : 'shortWeekdayTime';
    }
    return this.dateTime(time, format);
  }
  formatTimeRange({
    startTime,
    endTime,
    zoneType
  }) {
    const hideMeridiemForStart = this.hideMeridiemForStart({
      startTime,
      endTime
    });
    return {
      startTime: this.formatTime(startTime, {
        hideMeridiem: hideMeridiemForStart
      }),
      endTime: this.formatTime(endTime, {
        zoneType
      })
    };
  }
  formatTimeRangeWithWeekday({
    startTime,
    endTime
  }) {
    const start = asMoment(startTime);
    const end = asMoment(endTime);
    const hideMeridiemForStart = this.hideMeridiemForStart({
      startTime: start,
      endTime: end
    });
    const showBothWeekdays = !start.clone().startOf('day').isSame(end.clone().startOf('day'));
    const formattedStart = this.showsWeekdayBeforeHour || showBothWeekdays ? this.formatWeekdayAndTime(start, {
      hideMeridiem: hideMeridiemForStart
    }) : this.formatTime(start, {
      hideMeridiem: hideMeridiemForStart
    });
    const formattedStop = !this.showsWeekdayBeforeHour || showBothWeekdays ? this.formatWeekdayAndTime(end) : this.formatTime(end);
    return {
      startTime: formattedStart,
      endTime: formattedStop
    };
  }
  hideMeridiemForStart({
    startTime,
    endTime
  }) {
    const startMoment = asMoment(startTime);
    const endMoment = asMoment(endTime);
    if (!startMoment.isSame(endMoment, 'day')) {
      return false;
    }
    const startIsAm = startMoment.hours() < 12;
    const endIsAm = endMoment.hours() < 12;
    return startIsAm === endIsAm;
  }

  // parse localized date formats like 'shortDate'
  parseDatetime(str, formatName) {
    const formatString = this._formatDate[formatName];
    assert(`Please use a supported format name. You passed ${typeof formatName}. Supported format names are: ${this._displaySupportedFormatNames}.`, formatString);
    return moment(str, formatString);
  }
  millisecondsFromNowToDate(futureDate) {
    const futureMoment = asMoment(futureDate);
    return futureMoment.diff(moment(), 'milliseconds');
  }
  millisecondsFromDateToNow(pastDate) {
    const pastMoment = asMoment(pastDate);
    return moment().diff(pastMoment, 'milliseconds');
  }
  formats() {
    return this._localizedFormats;
  }
  get is12Hour() {
    /*
     * Matches any 'h' character such that:
     * the ']' character following it has a '[' between it and the 'h'.
     *
     * Assumes any ']' character is preceded at some point by a '['
     * and doesn't handle nested '[]' sections.
     */
    const is12HourRegex = /h(?![^[]*])/;
    return is12HourRegex.test(this._localizedFormats.shortTime);
  }

  /**
   * In some formats (like JP) the weekday is shown before the hour when both are used.
   * In most others, the weekday is shown first.
   */
  get showsWeekdayBeforeHour() {
    const format = this._localizedFormats.shortWeekdayTime;
    /*
     * Matches any 'h' or 'H' character such that:
     * the ']' character following it has a '[' between it and the 'h'.
     *
     * Assumes any ']' character is preceded at some point by a '['
     * and doesn't handle nested '[]' sections.
     */
    const hourIndex = format.search(/([Hh])(?![^[]*])/);
    assert('Format should include an hour', hourIndex >= 0);
    /*
     * Matches any 'ddd' string such that:
     * the ']' character following it has a '[' between it and the 'h'.
     *
     * Assumes any ']' character is preceded at some point by a '['
     * and doesn't handle nested '[]' sections.
     */
    const weekdayIndex = format.search(/(ddd)(?![^[]*])/);
    assert('Format should include the weekday', weekdayIndex >= 0);
    return weekdayIndex < hourIndex;
  }
}, (_descriptor = _applyDecoratedDescriptor(_class.prototype, "i18n", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "override_locale_start_of_week", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
})), _class));
export { LocaleTimeService as default };